import React, { useState } from 'react';
import { ArrowLeft, Send, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { customerApi } from '../services/customerApi';
import logo from '../assets/logo.gif';

interface FormData {
  message: string;
}

interface SubmissionResult {
  success: boolean;
  feedbackId?: string;
  message?: string;
  error?: string;
}

const FeedbackForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.message.trim()) return;

    setStatus('submitting');
    setSubmissionResult(null);

    try {
      const response = await customerApi.sendFeedback({
        message: formData.message.trim(),
        userEmail: user?.email || '',
        userName: user?.displayName || user?.email?.split('@')[0] || 'User'
      });

      setSubmissionResult({
        success: true,
        feedbackId: response.data?.feedbackId,
        message: response.message
      });
      setStatus('success');
      setShowSuccessModal(true);
      setFormData({ message: '' });
    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.'
      });
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setSubmissionResult(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleReturnToDashboard = () => {
    setShowSuccessModal(false);
    navigate('/dashboard');
  };

  const isFormValid = formData.message.trim();
  const isSubmitting = status === 'submitting';

  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161513] rounded-none shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 border border-[#0CB4E4]/30">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#0CB4E4]/30">
            <Check className="w-8 h-8 text-[#0CB4E4]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Feedback Submitted Successfully!</h3>
          <p className="text-gray-300 mb-4">Thank you for taking the time to share your feedback.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReturnToDashboard}
            className="flex-1 px-4 py-2 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 transition-colors font-medium shadow-md"
          >
            Return to Dashboard
          </button>
          <button
            onClick={() => setShowSuccessModal(false)}
            className="px-4 py-2 border border-[#0CB4E4]/30 text-white rounded-xl hover:bg-[#161513] transition-colors"
          >
            Submit More
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#161513] py-8 px-4 relative">
      <div className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none" style={{ backgroundImage: `url(${logo})` }}></div>
      <div className="max-w-2xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-bold text-3xl text-white">Feedback</h1>
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>

        {status === 'error' && submissionResult?.error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-400 mb-1">Unable to Submit Feedback</h3>
                <p className="text-sm text-red-300 mb-3">{submissionResult.error}</p>
                <button onClick={handleRetry} className="text-sm bg-red-900/30 text-red-300 px-3 py-1 rounded-md hover:bg-red-900/50 transition-colors border border-red-500/30">Try Again</button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#161513] rounded-none shadow-lg border border-[#0CB4E4]/30 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">We value your feedback</h2>
            <p className="text-sm text-gray-300">Let us know what you think about CR3ATE.AI and how we can improve.</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">From</label>
              <div className="px-4 py-3 bg-[#161513] border border-[#0CB4E4]/30 rounded-none text-white">{user?.email || 'Not logged in'}</div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-white mb-2">Your Feedback <span className="text-red-400">*</span></label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Share your thoughts, suggestions, or report any issues here..."
                rows={8}
                className="w-full px-4 py-3 border rounded-none bg-[#161513] border-[#0CB4E4]/30 focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white resize-vertical"
                maxLength={2000}
              />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-gray-400">{formData.message.length}/2000 characters</div>
              </div>
            </div>

            <div className="pt-4">
              <p className="text-sm text-gray-400 mb-4"><span className="text-red-400">*</span> Required fields</p>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-xl hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 focus:ring-2 focus:ring-[#0CB4E4] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#161513] border-t-transparent rounded-full animate-spin" />
                    Submitting Feedback...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showSuccessModal && <SuccessModal />}
    </div>
  );
};

export default FeedbackForm; 
